import { tz } from '@date-fns/tz'
import type { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'

// Subpath, not the root: CURRENT_TIME_ZONE runs at import, and tests that mock
// `@buffer-mono/popcorn` without requireActual would drop these functions.
import {
  formatTimeZone,
  getDefaultTimeZone,
} from '@buffer-mono/popcorn/src/helpers/timeZone'

export const CURRENT_TIME_ZONE = getDefaultTimeZone()

/**
 * Options for formatting date and time.
 */
export type DateTimeFormatterOptions = {
  /** The time zone to use for formatting. */
  timeZone?: string
  /** The time format to use (12 or 24 hour). */
  timeFormat?: '24' | '12'
  /** The date format to use. */
  dateFormat?: string
  /** Whether to include the time zone in the output. */
  includeTimeZone?: boolean
}

const defaultOptions = {
  timeZone: CURRENT_TIME_ZONE,
  timeFormat: '24',
  dateFormat: 'MMM d,',
  includeTimeZone: true,
  includeDate: true,
  includeTime: true,
}

/**
 * Returns a UTC string representation of a date.
 *
 * TZDate.toISOString ends with the zone's offset (`+00:00` for UTC) rather than
 * `Z`, so this converts through Date.
 *
 * @param {TZDate | Date} date - The date to convert to UTC.
 * @returns {string} The UTC string representation of the date.
 */
export const getUTCString = (date: TZDate | Date): string =>
  new Date(date.getTime()).toISOString()

/**
 * Formats a date and time string according to the provided options.
 * @param {string} date - The date string to format.
 * @param {DateTimeFormatterOptions & { includeDate?: boolean, includeTime?: boolean }} options - Formatting options.
 * @returns {string} The formatted date and time string.
 */
export const formatDateTime = (
  date: string,
  options: DateTimeFormatterOptions & {
    includeDate?: boolean
    includeTime?: boolean
  } = {},
): string => {
  const {
    timeZone,
    timeFormat,
    dateFormat,
    includeDate,
    includeTime,
    includeTimeZone,
  } = { ...defaultOptions, ...options }
  const selectedTimeFormat = timeFormat === '24' ? 'HH:mm' : 'h:mm a'
  let dateTimeFormat = `${dateFormat} ${selectedTimeFormat}`
  if (!includeDate) {
    dateTimeFormat = selectedTimeFormat
  }
  if (!includeTime) {
    dateTimeFormat = dateFormat
  }

  const formattedDate = format(date, dateTimeFormat, {
    in: tz(timeZone),
  })

  if (!includeTimeZone) {
    return formattedDate
  }
  return `${formattedDate} (${formatTimeZone(timeZone)})`
}

/**
 * Formats a date string to show only the time.
 * @param {string} date - The date string to format.
 * @param {DateTimeFormatterOptions} options - Formatting options.
 * @returns {string} The formatted time string.
 */
export const formatTime = (
  date: string,
  options: DateTimeFormatterOptions = {},
): string => {
  return formatDateTime(date, {
    includeTimeZone: false,
    ...options,
    includeDate: false,
  })
}

/**
 * Formats a date string to show only the date.
 * @param {string} date - The date string to format.
 * @param {DateTimeFormatterOptions} options - Formatting options.
 * @returns {string} The formatted date string.
 */
export const formatDate = (
  date: string,
  options: DateTimeFormatterOptions = {},
): string => {
  return formatDateTime(date, { ...options, includeTime: false })
}

/**
 * Checks if a date is valid.
 * @param {string | number | Date} date - The date to check.
 * @returns {boolean} True if the date is valid, false otherwise.
 */
export const isValidDateTime = (date: string | number | Date): boolean => {
  if (date instanceof Date) {
    return !isNaN(date.getTime())
  }

  if (typeof date === 'number' || typeof date === 'string') {
    const parsedDate = new Date(date)
    return !isNaN(parsedDate.getTime())
  }

  return false
}

const UNIX_EPOCH = new Date('1970-01-01T00:00:00.000Z')

export const isUnixEpochDate = (date: string | number | Date): boolean => {
  const dateToCheck = date instanceof Date ? date : new Date(date)
  return dateToCheck.getTime() === UNIX_EPOCH.getTime()
}
